<template>
  <div v-if="data" class="flex flex-col gap-3">
    <div
      v-for="(day, i) in allocationsPerDay"
      :key="i"
      class="flex flex-col gap-2"
      :data-testid="`day-${formatDate(day.day, 'yyyy-MM-dd')}`"
    >
      <div class="text-sm text-primary">
        <span class="font-medium">{{ formatDate(day.day, 'cccc') }}</span>
        —
        {{ formatDate(day.day, getDateFormat()) }}
      </div>
      <div class="flex flex-col gap-2">
        <div v-for="transaction in day.transactions" :key="transaction.id">
          <AllocationTransactionListItem :transaction="transaction" />
        </div>
      </div>
    </div>
  </div>

  <EntityListExceptions
    :status="status"
    :error="error"
    :items-count="transactions.length"
    :no-items-text="t('noBookingFound')"
  />

  <div v-if="hasNextPage" class="flex justify-center pt-8">
    <CVButton outline :is-loading="isFetchingNextPage" @click="() => fetchNextPage()">
      {{ t('loadMore') }}
    </CVButton>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { useCurrentStations } from '@/hooks/use-current-stations';
import { DateTime } from 'luxon';
import { computed } from 'vue';
import AllocationTransactionListItem from './AllocationTransactionListItem.vue';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { formatDate } from '@/hooks/use-formatted-date';
import { getDateFormat } from '@/hooks/use-date-format';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import { useAllocationTransactions } from '@/queries/use-allocations.ts';
import {
  AllocationStatus,
  AllocationTransactionType,
  AllocationType,
} from '@carvia/ros-client-types';
import { groupByDateTimeUnit } from '@/utils/group-by-date-time-unit';

const props = defineProps<{
  filterTransactionTypes: AllocationTransactionType[];
  filterDateFrom?: Date | null;
  filterDateTo?: Date | null;
}>();

const { t } = useI18n();

const currentStations = useCurrentStations();

const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status, error } =
  useAllocationTransactions(
    computed(() => ({
      stationIds: currentStations.value,
      dateAfter: props.filterDateFrom
        ? (DateTime.fromJSDate(props.filterDateFrom).startOf('day').toJSDate() ?? undefined)
        : undefined,
      dateBefore:
        DateTime.fromJSDate(props.filterDateTo ?? new Date())
          .startOf('day')
          .plus({ day: 1 })
          .toJSDate() ?? undefined,
      transactionTypes: props.filterTransactionTypes,
    })),
    computed(() => !!currentStations.value),
  );

const transactions = useFlattenPaginatedData(data);

const allocationsPerDay = computed(() =>
  groupByDateTimeUnit(
    transactions.value,
    (transaction) => {
      if (transaction.status === AllocationStatus.HANDED_OVER) {
        return transaction.endDate;
      }
      return (
        transaction.transfers?.find(
          (transfer) => transfer.type === AllocationType.TRANSFER_DELIVERY,
        )?.startDate ?? transaction.startDate
      );
    },
    'day',
    'transactions',
  ),
);
</script>

<i18n lang="json">
{
  "en": {
    "noBookingFound": "No Bookings found"
  },
  "de": {
    "noBookingFound": "Es wurden keine Buchungen gefunden"
  },
  "es": {
    "noBookingFound": "No se encontraron reservas"
  }
}
</i18n>
